import { ApiError } from "../api/http_client.js";
import { getContentConfig } from "../api/requests/player.requests.js";
import { StatisticsStorage } from "../storage/statistics_storage.js";
import { PersistentCache } from "../offline/persistent_cache.js";

const CACHE_KEY = "events_unlock_v1";

export type GameEventID = "daily_challenge" | "daily_tasks" | "chain_offer" | "boss_levels" | "win_streak_tower" | "key_hunt" | "prize_shot";

export interface EventUnlock
{
  enabled: boolean;
  min_level: number;
}

export type EventsConfig = Record<GameEventID, EventUnlock>;

const EVENT_IDS: GameEventID[] = [ "daily_challenge", "daily_tasks", "chain_offer", "boss_levels", "win_streak_tower", "key_hunt", "prize_shot" ];

const DEFAULT_EVENTS_CONFIG: EventsConfig = {
  daily_challenge: { enabled: true, min_level: 1 },
  daily_tasks: { enabled: true, min_level: 1 },
  chain_offer: { enabled: true, min_level: 1 },
  boss_levels: { enabled: true, min_level: 1 },
  win_streak_tower: { enabled: true, min_level: 1 },
  key_hunt: { enabled: true, min_level: 1 },
  prize_shot: { enabled: true, min_level: 1 },
};

function asRecord( value: unknown ): Record<string, unknown>
{
  return value && typeof value === "object" && !Array.isArray( value )
    ? value as Record<string, unknown>
    : {};
}

function boolValue( value: unknown, fallback: boolean ): boolean
{
  return typeof value === "boolean" ? value : fallback;
}

function minLevelValue( value: unknown, fallback: number ): number
{
  const number = typeof value === "number" ? value : Number( value );
  return Number.isFinite( number ) && number >= 1 ? Math.floor( number ) : fallback;
}

function normalizeEventsConfig( raw: unknown ): EventsConfig
{
  const value = asRecord( raw );
  const config = { ...DEFAULT_EVENTS_CONFIG };
  for ( const id of EVENT_IDS )
  {
    const event = asRecord( value[id] );
    const defaults = DEFAULT_EVENTS_CONFIG[id];
    config[id] = {
      enabled: boolValue( event.enabled, defaults.enabled ),
      min_level: minLevelValue( event.min_level, defaults.min_level ),
    };
  }
  return config;
}

export class EventsConfigService
{
  static hasCachedConfig(): boolean
  {
    return PersistentCache.read<unknown>( CACHE_KEY ) !== null;
  }

  private static config: EventsConfig = EventsConfigService.readCached() ?? DEFAULT_EVENTS_CONFIG;
  private static loaded = EventsConfigService.readCached() !== null;
  private static loading: Promise<EventsConfig> | null = null;

  private static readCached(): EventsConfig | null
  {
    const raw = PersistentCache.read<unknown>( CACHE_KEY );
    return raw ? normalizeEventsConfig( raw ) : null;
  }

  static get(): EventsConfig
  {
    return this.config;
  }

  static isLoaded(): boolean
  {
    return this.loaded;
  }

  static async refresh(): Promise<EventsConfig>
  {
    if ( this.loading ) return this.loading;

    this.loading = ( async () =>
    {
      try
      {
        const server_config = await getContentConfig<unknown>( "events_unlock" );
        this.config = normalizeEventsConfig( server_config );
        this.loaded = true;
        PersistentCache.write( CACHE_KEY, this.config );
        console.log( "[EventsConfig] loaded", this.config );
      }
      catch ( e )
      {
        if ( e instanceof ApiError && e.status === 404 )
        {
          this.config = DEFAULT_EVENTS_CONFIG;
          this.loaded = true;
          PersistentCache.remove( CACHE_KEY );
          console.warn( "[EventsConfig] no server config, using defaults" );
        }
        else
        {
          console.warn( "[EventsConfig] failed to fetch config", e );
        }
      }
      finally
      {
        this.loading = null;
      }

      return this.config;
    } )();

    return this.loading;
  }

  static getCurrentLevel(): number
  {
    return Math.max( 1, Math.floor( StatisticsStorage.load().level ) );
  }

  static getUnlockLevel( id: GameEventID ): number
  {
    return this.config[id].min_level;
  }

  static isEventUnlocked( id: GameEventID, level = this.getCurrentLevel() ): boolean
  {
    if ( !this.loaded ) return false;
    const config = this.config[id];
    return config.enabled && level >= config.min_level;
  }
}
